import { AutoComplete } from "primereact/autocomplete";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { useChangeClaimPayerMutation } from "../../store/queries/claims";
import { useGetAllPayersQuery } from "../../store/queries/payers";
import { IClaim } from "../../types/claim";

interface IPayerSelectorProps {
  claim: IClaim;
  onClaimUpdated: (claim: IClaim) => void;
  onNavigateToClaim?: (claimId: string) => void;
}

interface IPayerOption {
  name: string;
  id: number;
}

const SelectorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PayerName = styled.span`
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const ManualBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  background-color: #e3f2fd;
  color: #1565c0;
  margin-left: 0.5rem;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6c757d;
  padding: 0.15rem 0.3rem;
  border-radius: 4px;
  font-size: 0.85rem;
  display: inline-flex;
  align-items: center;

  &:hover {
    background-color: #e9ecef;
    color: #495057;
  }
`;

export function PayerSelector({
  claim,
  onClaimUpdated,
  onNavigateToClaim,
}: IPayerSelectorProps) {
  const toastRef = useRef<Toast>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchValue, setSearchValue] = useState<string | IPayerOption>("");
  const [filteredPayers, setFilteredPayers] = useState<IPayerOption[]>([]);
  const isSelectingRef = useRef(false);

  const { data: payersData } = useGetAllPayersQuery();
  const [changeClaimPayer, { isLoading: isChanging }] =
    useChangeClaimPayerMutation();

  const payers: IPayerOption[] = (payersData?.data || payersData || []).map(
    (p: any) => ({
      name: p.name,
      id: p.id,
    })
  );

  const onPayerSearch = (e: any) => {
    const query = e.query.toLowerCase();
    const filtered = payers.filter((payer) =>
      payer.name.toLowerCase().includes(query)
    );
    setFilteredPayers(filtered);
  };

  const submitPayerChange = async (selectedPayer: IPayerOption) => {
    setIsEditing(false);
    setSearchValue("");

    if (selectedPayer.id === claim.payerId) {
      return;
    }

    try {
      const result = await changeClaimPayer({
        claimId: claim.id,
        payerId: selectedPayer.id,
      }).unwrap();

      toastRef.current?.show({
        severity: "success",
        summary: "Payer Updated",
        detail: `Matched payer changed to ${selectedPayer.name}. Claim has been reprocessed.`,
        life: 5000,
      });

      if (result.data) {
        onClaimUpdated(result.data);
        if (onNavigateToClaim && result.data.id !== claim.id) {
          onNavigateToClaim(result.data.id);
        }
      }
    } catch (error: any) {
      toastRef.current?.show({
        severity: "error",
        summary: "Failed to Change Payer",
        detail:
          error?.data?.messages?.[0] ||
          "An error occurred while changing the payer.",
        life: 8000,
      });
    }
  };

  // Close editor when clicking outside the component (including the dropdown overlay)
  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (isSelectingRef.current) {
      isSelectingRef.current = false;
      return;
    }
    // Check if click is inside our container or inside a PrimeReact overlay panel
    const target = e.target as HTMLElement;
    const isInsideContainer = containerRef.current?.contains(target);
    const isInsideOverlay = target.closest(".p-autocomplete-panel");
    if (!isInsideContainer && !isInsideOverlay) {
      setIsEditing(false);
      setSearchValue("");
    }
  }, []);

  useEffect(() => {
    if (!isEditing) {
      return undefined;
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isEditing, handleClickOutside]);

  if (isChanging) {
    return (
      <>
        <Toast ref={toastRef} />
        <SelectorContainer>
          <div style={{ width: "18px", height: "18px", flexShrink: 0 }}>
            <ProgressSpinner
              style={{ width: "18px", height: "18px" }}
              strokeWidth="4"
            />
          </div>
          <span style={{ color: "#6c757d", fontSize: "0.9rem" }}>
            Reprocessing with new payer...
          </span>
        </SelectorContainer>
      </>
    );
  }

  if (isEditing) {
    return (
      <>
        <Toast ref={toastRef} />
        <SelectorContainer ref={containerRef}>
          <AutoComplete
            value={searchValue}
            suggestions={filteredPayers}
            field="name"
            completeMethod={onPayerSearch}
            onChange={(e) => setSearchValue(e.value)}
            onSelect={(e) => {
              isSelectingRef.current = true;
              submitPayerChange(e.value as IPayerOption);
            }}
            placeholder="Search payers..."
            dropdown
            autoFocus
            style={{ width: "100%" }}
            inputStyle={{ width: "100%" }}
          />
        </SelectorContainer>
      </>
    );
  }

  return (
    <>
      <Toast ref={toastRef} />
      <SelectorContainer>
        <PayerName onClick={() => setIsEditing(true)}>
          {claim.clearPayerName || "-"}
        </PayerName>
        {claim.isPayerManuallySet && <ManualBadge>manual override</ManualBadge>}
        <EditButton
          onClick={() => setIsEditing(true)}
          title="Change matched payer"
        >
          <i className="pi pi-pencil" />
        </EditButton>
      </SelectorContainer>
    </>
  );
}
